import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  linkedSignal,
  OnInit,
} from '@angular/core';

import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { AppSharedModule } from 'app/app-shared.module';

import { Permission } from 'app/providers/bridge-api/auth.service';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

export interface ConfigurationMoveEvent {
  originalGroupId: string;
  oldPermissionId: string;
  originalGroupName: string;
  newGroupName: string;
  newGroupId: string;
  newPermission: Permission;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-table',
  templateUrl: './configuration-table.component.html',
  styleUrls: ['./configuration-table.component.scss'],
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConfigurationTableComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly displayedColumns = input<any[]>();
  readonly configCreatorMap = input<any>();
  readonly configurations = input<SemantriaConfiguration[]>([]);
  readonly sortBy = input<string>('lastUsed');
  readonly sortDirection = input<'asc' | 'desc'>('desc');

  private groupService = inject(GroupService);

  readonly tableDataSource = linkedSignal(
    () => new MatTableDataSource(this.sortedConfigurations())
  );

  readonly sortedConfigurations = computed(() => {
    const direction = this.sort().direction === 'asc' ? 1 : -1;
    const propSort = (a, b, d = null) => {
      let dir = direction;
      if (d != null) {
        dir = d;
      }
      if (typeof a === 'string') {
        return a.localeCompare(b) * dir;
      } else {
        return (a - b) * dir;
      }
    };

    return [...this.configurations()].sort(
      (u1: SemantriaConfiguration, u2: SemantriaConfiguration) => {
        switch (this.sort().active) {
          case 'lang':
            let ret = propSort(u1.language_name, u2.language_name);
            if (!ret) {
              ret = propSort(u1.name, u2.name, 1);
            }
            return ret;
          case 'configName':
            return propSort(u1.name, u2.name);
          case 'lastUsed':
            return propSort(
              new Date(u1.last_processing_date || 0).getTime(),
              new Date(u2.last_processing_date || 0).getTime()
            );
          default:
            return 0;
        }
      }
    );
  });
  sort = linkedSignal<Sort>(() => ({ active: this.sortBy(), direction: this.sortDirection() }));
  groupId: string;

  ngOnInit() {
    this.groupId = this.groupService.getSelectedGroupId(true);
  }

  getCreator(config) {
    return this.configCreatorMap().get(config.creator_user_id);
  }

  onConfigurationSelected(config) {
    const url = `/groups/${this.groupId}/configurations/${config.id}`;

    window.open(url, '_blank');
  }
}
